/**
 * iOS WebKit (including Chrome/Firefox on iPhone) only allows audio.play() when
 * initiated from a user gesture on the *same* HTMLAudioElement that will play
 * the clip. A throwaway unlock Audio() does not transfer to a new element.
 *
 * Call createUnlockedAudioElement() synchronously inside the click/tap handler
 * (before any await), then assign the fetched blob to that element before play().
 */

const MATH_EQUATION_PATTERN = /\$[^$]+\$|\\\([^)]*\\\)|\$\$[^$]+\$\$|\\\[[^\]]*\\\]/;

export function textHasMathEquations(text: string): boolean {
  return MATH_EQUATION_PATTERN.test(text);
}

export function createUnlockedAudioElement(volume = 1): HTMLAudioElement {
  const audio = document.createElement('audio');
  audio.setAttribute('playsinline', 'true');
  audio.setAttribute('webkit-playsinline', 'true');
  audio.preload = 'auto';
  audio.volume = volume;
  audio.style.display = 'none';
  document.body.appendChild(audio);
  void audio.play().catch(() => {
    // Expected until media is loaded — primes this element for iOS WebKit
  });
  return audio;
}

/** Pause playback, revoke any blob URL, and remove the element from the DOM. */
export function releaseAudioElement(audio: HTMLAudioElement): void {
  audio.pause();
  audio.onended = null;
  audio.ontimeupdate = null;
  audio.onprogress = null;
  audio.oncanplay = null;

  if (audio.srcObject) {
    audio.srcObject = null;
  }

  if (audio.src.startsWith('blob:')) {
    URL.revokeObjectURL(audio.src);
  }

  audio.removeAttribute('src');
  audio.src = '';

  if (typeof audio.remove === 'function') {
    audio.remove();
  } else if (audio.parentNode) {
    audio.parentNode.removeChild(audio);
  }
}

export function waitForAudioCanPlay(audio: HTMLAudioElement, timeoutMs = 15_000): Promise<void> {
  if (audio.readyState >= HTMLMediaElement.HAVE_FUTURE_DATA) {
    return Promise.resolve();
  }

  return new Promise((resolve, reject) => {
    const timeout = setTimeout(() => {
      cleanup();
      // iOS WebKit may skip canplaythrough; proceed if any data is available
      if (audio.readyState >= HTMLMediaElement.HAVE_METADATA) {
        resolve();
        return;
      }
      reject(new Error('Audio load timed out'));
    }, timeoutMs);

    const onReady = () => {
      cleanup();
      resolve();
    };
    const onError = () => {
      cleanup();
      reject(new Error(`Audio failed to load: ${audio.error?.message ?? 'unknown error'}`));
    };
    const cleanup = () => {
      clearTimeout(timeout);
      audio.removeEventListener('canplaythrough', onReady);
      audio.removeEventListener('canplay', onReady);
      audio.removeEventListener('loadeddata', onReady);
      audio.removeEventListener('error', onError);
    };
    audio.addEventListener('canplaythrough', onReady, { once: true });
    audio.addEventListener('canplay', onReady, { once: true });
    audio.addEventListener('loadeddata', onReady, { once: true });
    audio.addEventListener('error', onError, { once: true });
  });
}

export async function loadAndPlayAudioBlob(audio: HTMLAudioElement, blob: Blob): Promise<void> {
  const mimeType = blob.type || 'audio/mpeg';
  const typedBlob = blob.type ? blob : new Blob([blob], { type: mimeType });
  const audioUrl = URL.createObjectURL(typedBlob);
  audio.pause();
  audio.src = audioUrl;
  audio.load();
  await waitForAudioCanPlay(audio);
  await audio.play();
}
